// Custom teal theme CSS variables to match the app's design
// Using the app's exact teal colors from tailwind.config.cjs
export const tealThemeOverrides = `
  :root {
    --hello-csv-color-primary: #187A90;
    --hello-csv-color-primary-light: #2BABC9;
    --hello-csv-color-primary-extra-light: #CFECF1;
    --hello-csv-color-secondary: #15687B;
    --hello-csv-color-tertiary: #2BABC9;
    --hello-csv-color-danger: #dc2626;
    --hello-csv-color-danger-light: #ef4444;
  }

  /* Make the importer root fill its bounded wrapper as a flex column so its */
  /* internal table region (min-h-0 flex-1 overflow-auto) shrinks and scrolls */
  /* instead of pushing the footer action button below the fold. */
  .hello-csv {
    display: flex !important;
    flex-direction: column !important;
    height: 100% !important;
    min-height: 0 !important;
  }

  /* Fix flex conflicts: HelloCSV uses Tailwind v4 utilities */
  /* that may conflict with or be missing from the app's Tailwind v3 */
  .hello-csv .flex-1 {
    flex: 1 1 0% !important;
  }

  .hello-csv .flex-2 {
    flex: 2 !important;
  }

  @media (min-width: 64rem) {
    .hello-csv .lg\\:flex-2 {
      flex: 2 !important;
    }
  }

  /* Force show desktop tabs and hide mobile dropdown for sheet selector */
  /* HelloCSV uses sm:hidden for mobile dropdown, hidden sm:block for desktop tabs */
  /* Be careful not to hide error tooltips - only hide the sheet selector dropdown */

  .hello-csv .hidden.sm\\:block {
    display: block !important;
  }

  /* Hide mobile sheet selector dropdown (has sm:hidden class) */
  /* This ensures the mobile dropdown stays hidden on desktop */
  /* IMPORTANT: Exclude dialog elements from this rule */
  .hello-csv .sm\\:hidden:not([id^="headlessui-dialog"]):not([id^="headlessui-dialog"] *) {
    display: none !important;
  }

  /* This importer only ever has one sheet, so present its selector as a step */
  /* header (matching the "Upload a file" / "Review and confirm" steps) rather */
  /* than a tab: large near-black text, no underline, not clickable-looking. */
  .hello-csv nav[role="tablist"] {
    display: flex !important;
    border-bottom: none !important;
    margin-top: 0.5rem !important;
    margin-bottom: 0.75rem !important;
  }

  .hello-csv nav[role="tablist"] button,
  .hello-csv nav[role="tablist"] button[aria-selected="true"],
  .hello-csv nav[role="tablist"] button:hover {
    border: none !important;
    background: transparent !important;
    border-radius: 0 !important;
    padding: 0 !important;
    font-size: 1.5rem !important;
    font-weight: 400 !important;
    color: #212121 !important;
    cursor: default !important;
    white-space: nowrap !important;
  }

  /* Drop the divider line hello-csv draws under the sheet-selector so the */
  /* header matches the Upload / Review steps (no rule under their title). */
  .hello-csv div:has(> nav[role="tablist"]) {
    border-bottom: none !important;
    padding-bottom: 0 !important;
  }

  /* This modal uses bodyPadding="p-0", so the Modal's float-right close button */
  /* strands itself at the very top above the content. Anchor it to the */
  /* top-right corner like our standard modals (the panel is position:relative). */
  [data-test-modal-body]:has(.hello-csv) [data-test-close-button] {
    position: absolute !important;
    top: 1.25rem !important;
    right: 1.5rem !important;
    margin: 0 !important;
    padding: 0 !important;
    z-index: 20 !important;
  }

  /* ========== BUTTON GROUP STYLING (All/Valid/Invalid) ========== */

  /* Button group container - cleaner rounded corners */
  .hello-csv .rounded-md.shadow-xs {
    box-shadow: none !important;
  }

  /* All buttons in the group - thinner borders */
  .hello-csv span.rounded-md button {
    font-size: 0.8125rem !important;
    padding: 0.375rem 0.75rem !important;
    border-color: #e5e7eb !important;
  }

  /* Make "Invalid" button text RED - target the danger variant button */
  .hello-csv span.rounded-md button.text-hello-csv-danger,
  .hello-csv span.rounded-md button[class*="danger"] {
    color: #dc2626 !important;
  }

  /* Active button in group (All/Valid) — use platform primary (teal), not black */
  .hello-csv span.rounded-md button[aria-current="true"] {
    background-color: var(--hello-csv-color-primary) !important;
    color: white !important;
    border-color: var(--hello-csv-color-primary) !important;
  }

  /* Active Invalid button - RED background when selected */
  .hello-csv span.rounded-md button.text-hello-csv-danger[aria-current="true"],
  .hello-csv span.rounded-md button[class*="danger"][aria-current="true"] {
    background-color: #dc2626 !important;
    color: white !important;
    border-color: #dc2626 !important;
  }

  /* ========== SEARCH INPUT ========== */

  /* Only target the actual search input (has Search placeholder) */
  .hello-csv input[placeholder*="Search"] {
    font-size: 0.8125rem !important;
    padding: 0.375rem 0.75rem 0.375rem 2.25rem !important;
    border: 1px solid #e5e7eb !important;
    border-radius: 0.375rem !important;
  }

  /* Search icon - make it smaller */
  .hello-csv input[placeholder*="Search"] + svg,
  .hello-csv input[placeholder*="Search"] ~ svg,
  .hello-csv label:has(input[placeholder*="Search"]) svg,
  .hello-csv div:has(> input[placeholder*="Search"]) svg {
    height: 1rem !important;
    width: 1rem !important;
  }

  /* Generic text inputs (not search) - normal padding */
  .hello-csv input[type="text"]:not([placeholder*="Search"]) {
    font-size: 0.8125rem !important;
    padding: 0.375rem 0.5rem !important;
    border: 1px solid #e5e7eb !important;
    border-radius: 0.375rem !important;
  }

  .hello-csv input:focus {
    outline: 2px solid var(--hello-csv-color-primary) !important;
    outline-offset: -1px !important;
    border-color: transparent !important;
  }

  /* ========== TABLE STYLING ========== */

  /* Force collapse on all table-related selectors */
  .hello-csv table,
  .hello-csv table.border-separate,
  .hello-csv .border-separate,
  .hello-csv [class*="border-separate"] {
    border-collapse: collapse !important;
    border-spacing: 0 0 !important;
    font-size: 0.8125rem !important;
    box-shadow: none !important;
  }

  /* Reset all table element spacing */
  .hello-csv thead,
  .hello-csv tbody,
  .hello-csv tfoot,
  .hello-csv tr,
  .hello-csv th,
  .hello-csv td {
    margin: 0 !important;
    padding: 0 !important;
    box-shadow: none !important;
  }

  /* Table header */
  .hello-csv thead {
    background-color: #f9fafb !important;
  }

  /* Header cells */
  .hello-csv th {
    font-size: 0.8125rem !important;
    font-weight: 500 !important;
    padding: 0.625rem 0.75rem !important;
    border: none !important;
    border-top: 1px solid #e5e7eb !important;
    border-bottom: 1px solid #e5e7eb !important;
    color: #374151 !important;
    background-color: #f9fafb !important;
  }

  /* Table body cells - skip first td */
  .hello-csv td:not(:first-child) {
    font-size: 0.8125rem !important;
    padding: 0.625rem 0.75rem !important;
    border: none !important;
    border-bottom: 1px solid #e5e7eb !important;
    color: #374151 !important;
    background-color: white !important;
  }

  /* First td (checkbox/row number) - minimal styling */
  .hello-csv td:first-child {
    border: none !important;
    border-bottom: 1px solid #e5e7eb !important;
    background-color: white !important;
  }

  /* Checkbox column - make it narrower */
  .hello-csv th:first-child,
  .hello-csv td:first-child {
    width: 60px !important;
    min-width: 60px !important;
    max-width: 60px !important;
    padding-left: 0.75rem !important;
    padding-right: 0.5rem !important;
  }

  /* Row hover */
  .hello-csv tbody tr:hover td {
    background-color: #f9fafb !important;
  }

  /* ========== TOOLBAR ICONS ========== */

  .hello-csv svg.h-6.w-6 {
    height: 1.25rem !important;
    width: 1.25rem !important;
  }

  /* ========== FILTER DROPDOWN ========== */

  .hello-csv select,
  .hello-csv .min-w-48 {
    font-size: 0.8125rem !important;
    border: 1px solid #e5e7eb !important;
    border-radius: 0.375rem !important;
    padding-left: 0.5rem !important;
    padding-right: 1.5rem !important;
    margin-left: 0 !important;
    min-width: 230px !important;
  }

  /* Fix filter dropdown container spacing */
  .hello-csv select {
    text-indent: 0 !important;
    background-position: right 0.5rem center !important;
  }

  /* ========== BACK/UPLOAD BUTTONS ========== */

  .hello-csv div[role="button"] {
    font-size: 0.8125rem !important;
    padding: 0.5rem 1rem !important;
  }

  /* Back button - secondary style */
  .hello-csv div[role="button"].bg-white {
    border: 1px solid #e5e7eb !important;
  }

  /* ========== GENERAL SPACING ========== */

  .hello-csv .my-5 {
    margin-top: 1rem !important;
    margin-bottom: 1rem !important;
  }

  .hello-csv .gap-5 {
    gap: 1rem !important;
  }

  /* ========== FIX CHECKBOX COLUMN WIDTH ========== */

  /* Target the checkbox column specifically */
  .hello-csv table th:first-of-type,
  .hello-csv table td:first-of-type {
    width: 50px !important;
    min-width: 50px !important;
    max-width: 70px !important;
  }

  /* Ensure cells don't have extra padding causing width issues */
  .hello-csv .sticky.left-0 {
    width: 50px !important;
    min-width: 50px !important;
  }

  /* ========== DIALOG/MODAL Z-INDEX FIX ========== */
  /* HelloCSV dialogs need higher z-index to appear above our Modal (z-50) */
  .fixed.inset-0.z-10.w-screen {
    z-index: 60 !important;
  }

  /* HeadlessUI Dialog portal containers - target by ID pattern */
  [id^="headlessui-dialog-"],
  [id^="headlessui-portal-"] {
    z-index: 60 !important;
  }

  /* Dialog backdrop and content */
  [id^="headlessui-dialog-"] ~ div,
  [id^="headlessui-dialog-overlay-"] {
    z-index: 60 !important;
  }

  /* Dialog panel itself */
  [id^="headlessui-dialog-panel-"] {
    z-index: 61 !important;
  }

  /* Center the dialog vertically - override items-end */
  .fixed.inset-0.z-10.w-screen > .flex,
  .fixed.inset-0.w-screen[style*="z-index: 60"] > .flex {
    align-items: center !important;
  }

  /* ========== DIALOG LAYOUT FIX ========== */
  /* Force horizontal layout for the dialog content */
  [id^="headlessui-dialog-panel-"] > div:first-child {
    display: flex !important;
    align-items: flex-start !important;
  }

  /* Icon container - keep on left, remove centering */
  [id^="headlessui-dialog-panel-"] > div:first-child > div:first-child {
    margin-left: 0 !important;
    margin-right: 0 !important;
    flex-shrink: 0 !important;
  }

  /* Text content - left align */
  [id^="headlessui-dialog-panel-"] > div:first-child > div:last-child {
    margin-top: 0 !important;
    margin-left: 1rem !important;
    text-align: left !important;
  }

  /* Title - left align */
  [id^="headlessui-dialog-panel-"] h3 {
    text-align: left !important;
  }

  /* Description - left align */
  [id^="headlessui-dialog-panel-"] p {
    text-align: left !important;
  }

  /* Buttons container - right align and center vertically */
  [id^="headlessui-dialog-panel-"] > div:last-child {
    display: flex !important;
    flex-direction: row-reverse !important;
    justify-content: flex-start !important;
    align-items: center !important;
    gap: 0.75rem !important;
  }

  /* Button wrappers - remove top margin */
  [id^="headlessui-dialog-panel-"] > div:last-child > div {
    margin-top: 0 !important;
    margin-left: 0 !important;
  }

  /* ========== VALIDATION ERROR DISPLAY ========== */

  /* Error tooltip/popover - high z-index to appear above modal */
  .hello-csv [id^="headlessui-popover-panel-"],
  .hello-csv [role="tooltip"] {
    z-index: 9999 !important;
  }

  /* Error text styling */
  .hello-csv .text-hello-csv-danger,
  .hello-csv .text-red-500,
  .hello-csv .text-red-600 {
    color: #dc2626 !important;
  }

  /* Error popover styling */
  .hello-csv [id^="headlessui-popover-panel-"] {
    background: white !important;
    border: 1px solid #fca5a5 !important;
    border-radius: 0.375rem !important;
    padding: 0.75rem !important;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1) !important;
    max-width: 300px !important;
  }

  /* ========== VALIDATION ERROR TOOLTIP ========== */
  /* HelloCSV uses hidden + group-hover:block pattern for error tooltips */
  /* The tooltip span has: hidden group-focus-within:block group-hover:block */

  /* Ensure group containers have relative positioning */
  .hello-csv .group {
    position: relative;
  }

  /* Error tooltip - show on group hover/focus */
  /* The element has 'hidden' class (display: none) and 'group-hover:block' */
  /* We need to override 'hidden' when the group is hovered */
  .hello-csv .group:hover > .hidden.group-hover\\:block,
  .hello-csv .group:hover .hidden.group-hover\\:block,
  .hello-csv .group:focus-within > .hidden.group-focus-within\\:block,
  .hello-csv .group:focus-within .hidden.group-focus-within\\:block {
    display: block !important;
  }

  /* Also support the combined class pattern */
  .hello-csv .group:hover [class*="hidden"][class*="group-hover:block"],
  .hello-csv .group:focus-within [class*="hidden"][class*="group-focus-within:block"] {
    display: block !important;
  }

  /* Ensure tooltip has high z-index and proper positioning */
  .hello-csv td .absolute,
  .hello-csv .group .absolute {
    z-index: 100;
  }

  /* Style the error tooltip */
  .hello-csv .group span.absolute.outline-hello-csv-danger {
    z-index: 100;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  }

  /* ========== CELL EDITABILITY & ACCESSIBILITY ========== */

  /* Indicate cells are editable with text cursor */
  .hello-csv td button {
    cursor: text !important;
  }

  /* Clear focus ring for keyboard navigation */
  .hello-csv td button:focus {
    outline: 2px solid var(--hello-csv-color-primary) !important;
    outline-offset: -2px !important;
  }

  /* Subtle background on focus for better visibility */
  .hello-csv td button:focus {
    background-color: rgba(24, 122, 144, 0.05) !important;
  }

  /* ========== PRIMARY BUTTON HOVER (match platform buttons) ========== */
  /* HelloCSV's primary CTA brightens on hover (bg-primary -> primary-light). */
  /* Platform buttons keep their color and dim to 90% opacity with a smooth */
  /* transition; mirror that so the button feels native to the app. */
  .hello-csv .bg-hello-csv-primary.hover\\:bg-hello-csv-primary-light {
    transition: opacity 0.15s ease-in-out !important;
  }

  .hello-csv .bg-hello-csv-primary.hover\\:bg-hello-csv-primary-light:hover {
    background-color: var(--hello-csv-color-primary) !important;
    opacity: 0.9 !important;
  }

  /* ========== INFO CALLOUT (match platform FlashInfo) ========== */
  /* HelloCSV's required-columns note is teal text on light teal (low */
  /* contrast). Use the app's official info style: near-black text on a */
  /* light blue-gray background with a teal border. */
  .hello-csv .bg-hello-csv-primary-extra-light.rounded-md.p-4 {
    background-color: #dae4ef !important;
    border: 1px solid #187a90 !important;
  }

  .hello-csv .bg-hello-csv-primary-extra-light.rounded-md.p-4,
  .hello-csv .bg-hello-csv-primary-extra-light.rounded-md.p-4 .text-hello-csv-primary {
    color: rgba(0, 0, 0, 0.7) !important;
  }
`;
